import { closeModal, openModal } from '@components/modals';
import { execOnLoad } from '@general/helpers';
import './mobile-menu.scss';
import breakpoints from 'breakpoints';

if (!window.dAgency) window.dAgency = {};

class MobileMenu {
  constructor(context) {
    this.context = context;
    this.elements = this.cacheElements();

    if (!this.isValidSetup()) return;

    this.state = {
      isOpen: false,
      currentLevel: 1,
      enableTransparency:
        this.elements.header?.hasAttribute('data-transparent') || false,
    };

    this.mediaQuery = window.matchMedia(`(min-width: ${breakpoints.sm})`);
    this.init();
  }

  cacheElements() {
    const container = this.context.classList.contains('mobile-menu__container')
      ? this.context
      : this.context.closest('.mobile-menu__container');

    if (!container) return {};

    return {
      container,
      header: document.querySelector('.header__background'),
      menuButton: container.querySelector('.mobile-menu__icon'),
      modal: container.querySelector('.modal'),
      closeIcons: container.querySelectorAll('.icon-menu-close'),
      firstLevelItems: container.querySelectorAll(
        '.mobile-menu__first-level--openable',
      ),
      secondLevelItems: container.querySelectorAll(
        '.mobile-menu__second-level--openable',
      ),
      backFirstMenus: container.querySelectorAll('.mobile-menu__second-level'),
      backSecondMenu: container.querySelectorAll('.item-second-level-span'),
      breadcrumbSpan: container.querySelector(
        '.mobile-menu__second-level-breadcrumbs .item-third-level-span',
      ),
    };
  }

  isValidSetup() {
    return (
      this.elements.container && this.elements.menuButton && this.elements.modal
    );
  }

  init() {
    this.setupEventListeners();
    this.setupMutationObserver();
    this.setupMediaQueryListener();
  }

  setupEventListeners() {
    // Main menu toggle
    this.elements.menuButton.addEventListener('click', () => this.toggleMenu());

    // Close icons
    this.elements.closeIcons.forEach((icon) => {
      icon.addEventListener('click', () => this.closeMenu());
    });

    // First level navigation
    this.setupFirstLevelNavigation();

    // Second level navigation
    this.setupSecondLevelNavigation();

    // Back navigation
    this.setupBackNavigation();
  }

  setupFirstLevelNavigation() {
    this.elements.firstLevelItems.forEach((item) => {
      const secondLevel = item.querySelector('.mobile-menu__second-level');
      const opener = item.querySelector('.mobile-menu__first-level-link');

      if (!secondLevel || !opener) return;

      opener.addEventListener('click', () => {
        this.closeAllSiblings(
          item,
          '.mobile-menu__first-level--openable',
          '.mobile-menu__second-level',
        );
        this.openLevel(secondLevel);
      });
    });
  }

  setupSecondLevelNavigation() {
    this.elements.secondLevelItems.forEach((item) => {
      const thirdLevel = item.querySelector('.mobile-menu__third-level');
      const opener = item.querySelector('.mobile-menu__second-level-link');

      if (!thirdLevel || !opener) return;

      opener.addEventListener('click', () => {
        const currentItemLabel = opener.querySelector('span')?.innerText || '';

        this.closeAllSiblings(
          item,
          '.mobile-menu__second-level--openable',
          '.mobile-menu__third-level',
        );

        item?.previousSibling?.classList?.remove('current');
        item?.nextElementSibling?.classList?.remove('current');
        item.classList.add('current');
        this.openThirdLevel(thirdLevel, currentItemLabel);
      });
    });
  }

  setupBackNavigation() {
    // Back to first level
    this.elements.backFirstMenus.forEach((menu) => {
      menu.querySelectorAll('.item-home').forEach((item) => {
        item.addEventListener('click', (e) => {
          e.preventDefault();
          this.navigateToFirstLevel();
        });
      });
    });

    // Back to second level
    this.elements.backSecondMenu.forEach((item) => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        this.navigateToSecondLevel();
      });
    });
  }

  setupMutationObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class'
        ) {
          const target = mutation.target;
          const hasOpen = target.classList.contains('open');
          const hadOpen = mutation.oldValue?.includes('open');

          if (!hasOpen && hadOpen) {
            this.handleModalClose();
          }
        }
      });
    });

    observer.observe(this.elements.modal, {
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['class'],
    });
  }

  setupMediaQueryListener() {
    this.mediaQuery.addEventListener('change', () => {
      if (this.mediaQuery.matches && this.state.isOpen) {
        this.closeMenu();
      }
    });
  }

  toggleMenu() {
    if (this.state.isOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  openMenu() {
    this.handleAnnouncementBarScroll();

    this.elements.container.classList.add('open');
    openModal(this.elements.modal);
    this.updateTransparency(false);

    this.state.isOpen = true;
  }

  closeMenu() {
    this.elements.container.classList.remove('open');
    closeModal(this.elements.modal);
    this.updateTransparency(true);
    this.resetAllLevels();

    this.elements.secondLevelItems.forEach((item) => {
      item.classList.remove('current');
    });

    this.state.isOpen = false;
  }

  handleModalClose() {
    if (this.mediaQuery.matches && this.state.isOpen) {
      this.closeMenu();
    }
  }

  handleAnnouncementBarScroll() {
    const announcementHeight = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        '--announcement-bar-height',
      ),
    );

    if (window.scrollY < announcementHeight) {
      window.scrollTo({ top: announcementHeight, behavior: 'smooth' });
    }
  }

  closeAllSiblings(currentItem, siblingSelector, levelSelector) {
    const parentContainer = currentItem.parentElement;
    const siblings = parentContainer.querySelectorAll(siblingSelector);

    siblings.forEach((sibling) => {
      if (sibling !== currentItem) {
        const level = sibling.querySelector(levelSelector);
        level?.classList.remove('open');
      }
    });
  }

  openLevel(level) {
    level.classList.add('open');
  }

  openThirdLevel(thirdLevel, itemLabel) {
    // Show/hide elements for third level
    this.elements.container
      .querySelectorAll('.item-third-level')
      .forEach((item) => item.classList.remove('hide'));

    this.elements.container
      .querySelectorAll('.item-second-level')
      .forEach((item) => item.classList.add('third-opened'));

    // Update breadcrumb
    if (this.elements.breadcrumbSpan) {
      this.elements.breadcrumbSpan.innerText = itemLabel;
    }

    // Hide close icon
    const closeIcon = document.querySelector(
      '.mobile-menu__second-level.open .icon-menu-close',
    );
    closeIcon?.classList.add('hide');

    this.openLevel(thirdLevel);
    this.state.currentLevel = 3;
  }

  navigateToFirstLevel() {
    this.elements.container
      .querySelector('.mobile-menu__second-level.open')
      ?.classList.remove('open');
    this.elements.container
      .querySelector('.mobile-menu__third-level.open')
      ?.classList.remove('open');

    this.resetThirdLevelState();
    this.state.currentLevel = 1;
  }

  navigateToSecondLevel() {
    this.elements.container
      .querySelector('.mobile-menu__third-level.open')
      ?.classList.remove('open');
    this.resetThirdLevelState();
    this.state.currentLevel = 2;
    this.elements.secondLevelItems.forEach((item) => {
      item.classList.remove('current');
    });
    // Hide close icon
    const closeIcon = document.querySelector(
      '.mobile-menu__second-level.open .icon-menu-close',
    );
    closeIcon?.classList.remove('hide');
  }

  resetThirdLevelState() {
    this.elements.container
      .querySelectorAll('.item-second-level')
      .forEach((item) => item.classList.remove('third-opened'));

    this.elements.container
      .querySelectorAll('.item-third-level')
      .forEach((item) => item.classList.add('hide'));

    if (this.elements.breadcrumbSpan) {
      this.elements.breadcrumbSpan.innerText = '';
    }
  }

  resetAllLevels() {
    this.elements.container
      .querySelectorAll('.mobile-menu__second-level.open')
      .forEach((level) => level.classList.remove('open'));

    this.elements.container
      .querySelectorAll('.mobile-menu__third-level.open')
      .forEach((level) => level.classList.remove('open'));

    this.resetThirdLevelState();
    this.state.currentLevel = 1;
  }

  updateTransparency(transparent) {
    if (this.state.enableTransparency && this.elements.header) {
      this.elements.header.dataset.transparent = transparent.toString();
    }
  }
}

// Initialize function
window.dAgency.initMobileMenu = (context) => {
  new MobileMenu(context);
};

execOnLoad(window.dAgency.initMobileMenu, '.mobile-menu__container');
